import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatearCLP,
  formatearPorcentajeSimple,
  type PeriodoDashboard,
} from '@lexicore/shared';
import { GraficoBarras, GraficoTendencia } from '../componentes/Graficos';
import { Cargando, ErrorCarga, Panel, TarjetaKpi, Vacio } from '../componentes/UI';
import {
  IconoDescargar,
  IconoDocumento,
  IconoMas,
} from '../componentes/Iconos';
import {
  useDesempenoCategorias,
  useResumenGeneral,
  useSerieMensual,
} from '../lib/consultas';
import { ModalInformeEjecutivo } from './dashboard/ModalInformeEjecutivo';

const PERIODOS: { clave: PeriodoDashboard; etiqueta: string }[] = [
  { clave: 'este_mes', etiqueta: 'Este Mes' },
  { clave: 'trimestre', etiqueta: 'Último Trimestre' },
  { clave: 'ytd', etiqueta: 'Año en Curso (YTD)' },
  { clave: '12_meses', etiqueta: 'Últimos 12 Meses' },
  { clave: 'todos', etiqueta: 'Histórico Completo' },
];

type FiltroRadarFoco = 'TODAS' | 'CRITICAS' | 'LICITACIONES' | 'PROYECTOS' | 'FACTURAS';

export function Dashboard() {
  const navigate = useNavigate();
  const [periodo, setPeriodo] = useState<PeriodoDashboard>('12_meses');
  const [esRangoPersonalizado, setEsRangoPersonalizado] = useState(false);
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [filtroRadar, setFiltroRadar] = useState<FiltroRadarFoco>('TODAS');
  const [modalInformeAbierto, setModalInformeAbierto] = useState(false);

  const filtrosConsulta = esRangoPersonalizado
    ? { periodo, desde: fechaDesde || undefined, hasta: fechaHasta || undefined }
    : { periodo };

  const resumen = useResumenGeneral(filtrosConsulta);
  const serie = useSerieMensual(12);
  const categorias = useDesempenoCategorias();

  if (resumen.isLoading) return <Cargando texto="Cargando Torre de Control Ejecutiva…" />;
  if (resumen.isError)
    return (
      <ErrorCarga
        error={resumen.error}
        reintentar={() => void resumen.refetch()}
      />
    );
  if (!resumen.data) return null;

  const { pipeline, resultados, finanzas, cobranza, proyectos, radarFoco } = resumen.data;

  const descargarCSV = () => {
    const params = new URLSearchParams();
    params.set('periodo', periodo);
    if (esRangoPersonalizado) {
      if (fechaDesde) params.set('desde', fechaDesde);
      if (fechaHasta) params.set('hasta', fechaHasta);
    }
    window.open(`/api/analitica/informe-ejecutivo.csv?${params.toString()}`, '_blank');
  };

  // Filtrado reactivo del Radar de Foco
  const licitacionesFiltradas = radarFoco.licitacionesUrgentes.filter((lic) => {
    if (filtroRadar === 'CRITICAS') return lic.urgencia === 'CRITICA';
    if (filtroRadar === 'PROYECTOS' || filtroRadar === 'FACTURAS') return false;
    return true;
  });

  const proyectosFiltrados = radarFoco.proyectosEnRiesgo.filter((p) => {
    if (filtroRadar === 'CRITICAS') return p.urgencia === 'CRITICA';
    if (filtroRadar === 'LICITACIONES' || filtroRadar === 'FACTURAS') return false;
    return true;
  });

  const facturasFiltradas = radarFoco.facturasMoraCritica.filter((fac) => {
    if (filtroRadar === 'CRITICAS') return fac.urgencia === 'CRITICA';
    if (filtroRadar === 'LICITACIONES' || filtroRadar === 'PROYECTOS') return false;
    return true;
  });

  const totalContingenciasFiltradas =
    licitacionesFiltradas.length + proyectosFiltrados.length + facturasFiltradas.length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {/* ── CABECERA EJECUTIVA: SELECTOR DE PERÍODO Y ACCIONES RÁPIDAS ── */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 12,
          padding: '16px 20px',
          background: 'var(--bg-card)',
          borderRadius: 8,
          border: '1px solid var(--border-color)',
        }}
      >
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0, color: 'var(--text-primary)' }}>
            📊 Torre de Control Ejecutiva
          </h2>
          <p style={{ fontSize: 12.5, margin: '4px 0 0', color: 'var(--text-secondary)' }}>
            Cuadro de mando integral: visión comercial, operacional, rentabilidad y cobranza al Estado en tiempo real.
          </p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          {/* Selector de ventana temporal */}
          <div
            style={{
              display: 'inline-flex',
              background: 'var(--bg-muted)',
              borderRadius: 8,
              padding: 3,
              gap: 2,
              border: '1px solid var(--border-subtle)',
            }}
          >
            {PERIODOS.map((p) => {
              const activo = !esRangoPersonalizado && periodo === p.clave;
              return (
                <button
                  key={p.clave}
                  type="button"
                  onClick={() => {
                    setEsRangoPersonalizado(false);
                    setPeriodo(p.clave);
                  }}
                  style={{
                    padding: '6px 12px',
                    fontSize: 11.5,
                    fontWeight: activo ? 600 : 400,
                    borderRadius: 4,
                    border: 'none',
                    background: activo ? 'var(--accent-blue, #2563eb)' : 'transparent',
                    color: activo ? '#ffffff' : 'var(--text-secondary)',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {p.etiqueta}
                </button>
              );
            })}

            <button
              type="button"
              onClick={() => setEsRangoPersonalizado(true)}
              style={{
                padding: '6px 12px',
                fontSize: 11.5,
                fontWeight: esRangoPersonalizado ? 600 : 400,
                borderRadius: 4,
                border: 'none',
                background: esRangoPersonalizado ? 'var(--accent-blue, #2563eb)' : 'transparent',
                color: esRangoPersonalizado ? '#ffffff' : 'var(--text-secondary)',
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              📅 Rango
            </button>
          </div>

          {/* Selector de fechas si es personalizado */}
          {esRangoPersonalizado && (
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                background: 'var(--bg-card)',
                padding: '4px 8px',
                borderRadius: 6,
                border: '1px solid var(--accent-blue, #3b82f6)',
              }}
            >
              <input
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                style={{
                  padding: '4px 6px',
                  fontSize: 11,
                  background: 'var(--bg-muted)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 4,
                }}
                title="Fecha Desde"
              />
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>a</span>
              <input
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                style={{
                  padding: '4px 6px',
                  fontSize: 11,
                  background: 'var(--bg-muted)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: 4,
                }}
                title="Fecha Hasta"
              />
            </div>
          )}

          {/* Botón Ver Informe Ejecutivo */}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => setModalInformeAbierto(true)}
            title="Abrir reporte estructurado formal para Directorio y Bancos"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <IconoDocumento size={14} />
            <span>Ver Informe Ejecutivo</span>
          </button>

          {/* Botón Descargar CSV */}
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={descargarCSV}
            title="Descargar reporte formal en formato CSV para Microsoft Excel"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <IconoDescargar size={14} />
            <span>CSV (Excel)</span>
          </button>

          {/* Botón Nueva Oferta */}
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => navigate('/pipeline')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
          >
            <IconoMas size={14} />
            <span>Nueva Oferta</span>
          </button>
        </div>
      </div>

      {/* ── GRID SUPERIOR: 8 TARJETAS KPI MAESTRAS CON DRILL-DOWN ── */}
      <div className="kpi-grid">
        {/* 1. Total Adjudicado */}
        <TarjetaKpi
          etiqueta="Total adjudicado"
          valor={formatearCLP(resultados.totalAdjudicado)}
          acento="var(--accent-green)"
          colorValor="var(--accent-green)"
          nota={`${resultados.adjudicadas} ganadas · ${formatearCLP(resultados.ticketPromedio ?? 0)} ticket prom.`}
          onClick={() => navigate(resultados.drillDownUrl)}
        />

        {/* 2. Resultado Operacional Real */}
        <TarjetaKpi
          etiqueta="Resultado operacional"
          valor={formatearCLP(finanzas.resultadoOperacional)}
          acento="var(--accent-blue)"
          colorValor={
            finanzas.resultadoOperacional >= 0
              ? 'var(--accent-green)'
              : 'var(--accent-red)'
          }
          nota={
            finanzas.margenOperativoPct !== null
              ? `${formatearPorcentajeSimple(finanzas.margenOperativoPct, 1)} margen operativo real neto`
              : 'Descontando gastos generales'
          }
          onClick={() => navigate(finanzas.drillDownUrl)}
        />

        {/* 3. Por Cobrar (Estado) */}
        <TarjetaKpi
          etiqueta="Por cobrar (Estado)"
          valor={formatearCLP(cobranza.porCobrar)}
          acento="var(--accent-yellow)"
          colorValor={cobranza.vencido > 0 ? 'var(--accent-yellow)' : undefined}
          insignia={
            cobranza.vencido > 0
              ? {
                  texto: `🚨 ${formatearCLP(cobranza.vencido)} vencido`,
                  color: 'var(--accent-red)',
                  fondo: 'var(--accent-red-soft, rgba(239, 68, 68, 0.15))',
                }
              : undefined
          }
          nota="Clic para gestionar cartera y cobranza"
          onClick={() => navigate(cobranza.drillDownUrl)}
        />

        {/* 4. Win Rate Mercado Público */}
        <TarjetaKpi
          etiqueta="Win rate Mercado Público"
          valor={
            resultados.winRatePct === null
              ? 'Sin datos'
              : formatearPorcentajeSimple(resultados.winRatePct)
          }
          acento="var(--accent-purple)"
          nota={
            resultados.winRatePct === null
              ? 'Aún no hay procesos resueltos'
              : `${resultados.adjudicadas} ganadas · ${resultados.perdidas} perdidas`
          }
          onClick={() => navigate(pipeline.drillDownUrl)}
        />

        {/* 5. Pipeline en Juego */}
        <TarjetaKpi
          etiqueta="Pipeline en juego"
          valor={formatearCLP(pipeline.montoPipelineAbierto)}
          acento="var(--accent-blue)"
          colorValor="var(--accent-blue)"
          nota={`${pipeline.oportunidades} oportunidades · ${pipeline.ofertasEnviadas} enviadas`}
          onClick={() => navigate(pipeline.drillDownUrl)}
        />

        {/* 6. Proyectos Activos */}
        <TarjetaKpi
          etiqueta="Proyectos activos"
          valor={`${proyectos.enEjecucion} en ejecución`}
          acento="var(--accent-cyan, #06b6d4)"
          insignia={
            proyectos.enRiesgoConteo > 0
              ? {
                  texto: `⚠️ ${proyectos.enRiesgoConteo} en alerta >80%`,
                  color: '#f59e0b',
                  fondo: 'rgba(245, 158, 11, 0.15)',
                }
              : undefined
          }
          nota={`${proyectos.total} proyectos totales en cartera`}
          onClick={() => navigate(proyectos.drillDownUrl)}
        />

        {/* 7. Días Promedio de Cobro (DSO) */}
        <TarjetaKpi
          etiqueta="Días cobro (DSO)"
          valor={
            cobranza.diasPromedioCobro === null
              ? '—'
              : `${Math.round(cobranza.diasPromedioCobro)} días`
          }
          acento="var(--accent-gray)"
          nota={
            cobranza.diasPromedioCobro === null
              ? 'Aún sin facturas cobradas'
              : 'Ponderado por volumen financiero'
          }
          onClick={() => navigate(cobranza.drillDownUrl)}
        />

        {/* 8. Gastos de Estructura Fija */}
        <TarjetaKpi
          etiqueta="Gastos estructura fija"
          valor={formatearCLP(finanzas.costosGenerales)}
          acento="var(--accent-gray)"
          nota="Arriendos, licencias y costos fijos de empresa"
          onClick={() => navigate('/costos')}
        />
      </div>

      {/* ── FRANJA DE MÉTRICAS EJECUTIVAS C-LEVEL (DECISION STRIP) ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 12,
          padding: '12px 16px',
          background: 'var(--bg-card)',
          borderRadius: 8,
          border: '1px solid var(--border-color)',
        }}
      >
        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Ticket Promedio Adjudicado</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>
            {resultados.ticketPromedio ? formatearCLP(resultados.ticketPromedio) : '—'}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Duración Promedio Contrato</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>
            {resultados.duracionPromedioMeses ? `${resultados.duracionPromedioMeses} meses` : '—'}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Plazo Promedio Ofertado</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginTop: 2 }}>
            {resultados.plazoPromedioOfertadoDias ? `${resultados.plazoPromedioOfertadoDias} días corridos` : '—'}
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Flujo Mensualizado Proyectado</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-green, #10b981)', marginTop: 2 }}>
            {formatearCLP(finanzas.mrr)} / mes
          </div>
        </div>

        <div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Utilidad Bruta Operacional</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--accent-blue, #3b82f6)', marginTop: 2 }}>
            {formatearCLP(finanzas.utilidadBruta)} ({formatearPorcentajeSimple(finanzas.margenBrutoPct ?? 0, 1)})
          </div>
        </div>
      </div>

      {/* ── RADAR DE FOCO INMEDIATO CON FILTROS 1-CLIC ── */}
      <Panel
        titulo="🚨 Radar de Foco Inmediato (Contingencias que requieren decisión hoy)"
        subtitulo={`Total de contingencias en cartera: ${radarFoco.totalAlertas} (${radarFoco.alertasCriticas} críticas · ${radarFoco.alertasAdvertencia} advertencias)`}
        style={{
          borderLeft:
            radarFoco.alertasCriticas > 0
              ? '4px solid var(--accent-red, #ef4444)'
              : '4px solid var(--accent-yellow, #f59e0b)',
        }}
      >
        {/* Píldoras de filtro rápido del Radar */}
        <div
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            marginBottom: 16,
            paddingBottom: 12,
            borderBottom: '1px solid var(--border-color)',
          }}
        >
          <button
            type="button"
            onClick={() => setFiltroRadar('TODAS')}
            className={`btn btn-sm ${filtroRadar === 'TODAS' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: 11 }}
          >
            🌟 Todas las Alertas ({radarFoco.totalAlertas})
          </button>

          <button
            type="button"
            onClick={() => setFiltroRadar('CRITICAS')}
            className={`btn btn-sm ${filtroRadar === 'CRITICAS' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: 11 }}
          >
            🔴 Solo Críticas ({radarFoco.alertasCriticas})
          </button>

          <button
            type="button"
            onClick={() => setFiltroRadar('LICITACIONES')}
            className={`btn btn-sm ${filtroRadar === 'LICITACIONES' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: 11 }}
          >
            ⏳ Licitaciones ({radarFoco.licitacionesUrgentes.length})
          </button>

          <button
            type="button"
            onClick={() => setFiltroRadar('PROYECTOS')}
            className={`btn btn-sm ${filtroRadar === 'PROYECTOS' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: 11 }}
          >
            ⚠️ Proyectos en Riesgo ({radarFoco.proyectosEnRiesgo.length})
          </button>

          <button
            type="button"
            onClick={() => setFiltroRadar('FACTURAS')}
            className={`btn btn-sm ${filtroRadar === 'FACTURAS' ? 'btn-primary' : 'btn-secondary'}`}
            style={{ fontSize: 11 }}
          >
            🚨 Facturas en Mora ({radarFoco.facturasMoraCritica.length})
          </button>
        </div>

        {totalContingenciasFiltradas === 0 ? (
          <div
            style={{
              padding: 24,
              textAlign: 'center',
              color: 'var(--accent-green, #10b981)',
              fontWeight: 500,
              fontSize: 13,
            }}
          >
            ✅ No hay contingencias pendientes para el filtro seleccionado.
          </div>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 16,
            }}
          >
            {/* Bloque 1: Licitaciones por Cerrar */}
            {licitacionesFiltradas.length > 0 && (
              <div
                style={{
                  padding: 14,
                  background: 'var(--bg-muted, rgba(255,255,255,0.02))',
                  borderRadius: 6,
                  border: '1px solid var(--border-color)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                    ⏳ Licitaciones por Cerrar ({licitacionesFiltradas.length})
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Mercado Público</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {licitacionesFiltradas.map((lic) => (
                    <div
                      key={lic.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 10px',
                        background: 'var(--bg-card)',
                        borderRadius: 4,
                        border:
                          lic.urgencia === 'CRITICA'
                            ? '1px solid rgba(239, 68, 68, 0.4)'
                            : '1px solid var(--border-color)',
                      }}
                    >
                      <div style={{ maxWidth: '65%' }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {lic.nombre}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {lic.organismo} · {formatearCLP(lic.montoOfertadoNeto)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color:
                              lic.urgencia === 'CRITICA'
                                ? 'var(--accent-red)'
                                : 'var(--accent-yellow)',
                            display: 'block',
                          }}
                        >
                          {lic.horasRestantes <= 48
                            ? `Cierra en ${lic.horasRestantes}h`
                            : `${lic.diasRestantes} días`}
                        </span>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          style={{ fontSize: 10, padding: '2px 6px', marginTop: 4 }}
                          onClick={() => navigate(lic.drillDownUrl)}
                        >
                          Ir a Oferta →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bloque 2: Proyectos en Riesgo */}
            {proyectosFiltrados.length > 0 && (
              <div
                style={{
                  padding: 14,
                  background: 'var(--bg-muted, rgba(255,255,255,0.02))',
                  borderRadius: 6,
                  border: '1px solid var(--border-color)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                    ⚠️ Proyectos en Riesgo ({proyectosFiltrados.length})
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                    Consumo &gt;80% o sobrecosto
                  </span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {proyectosFiltrados.map((p) => (
                    <div
                      key={p.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 10px',
                        background: 'var(--bg-card)',
                        borderRadius: 4,
                        border:
                          p.urgencia === 'CRITICA'
                            ? '1px solid rgba(239, 68, 68, 0.4)'
                            : '1px solid var(--border-color)',
                      }}
                    >
                      <div style={{ maxWidth: '65%' }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {p.codigo} — {p.nombre}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          {p.organismo ?? 'Proyecto'} · Real: {formatearCLP(p.costoReal)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color:
                              p.tieneSobrecosto
                                ? 'var(--accent-red)'
                                : 'var(--accent-yellow)',
                            display: 'block',
                          }}
                        >
                          {p.consumoPct}% consumido
                        </span>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          style={{ fontSize: 10, padding: '2px 6px', marginTop: 4 }}
                          onClick={() => navigate(p.drillDownUrl)}
                        >
                          Auditar Costos →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Bloque 3: Facturas en Mora Crítica */}
            {facturasFiltradas.length > 0 && (
              <div
                style={{
                  padding: 14,
                  background: 'var(--bg-muted, rgba(255,255,255,0.02))',
                  borderRadius: 6,
                  border: '1px solid var(--border-color)',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 10,
                  }}
                >
                  <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
                    🚨 Facturas en Mora ({facturasFiltradas.length})
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Vencimiento superado</span>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {facturasFiltradas.map((fac) => (
                    <div
                      key={fac.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 10px',
                        background: 'var(--bg-card)',
                        borderRadius: 4,
                        border:
                          fac.urgencia === 'CRITICA'
                            ? '1px solid rgba(239, 68, 68, 0.4)'
                            : '1px solid var(--border-color)',
                      }}
                    >
                      <div style={{ maxWidth: '65%' }}>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--text-primary)',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {fac.numero ? `Factura ${fac.numero}` : 'Documento'} — {fac.organismo}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                          Saldo: {formatearCLP(fac.montoSaldo)}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            color:
                              fac.urgencia === 'CRITICA'
                                ? 'var(--accent-red)'
                                : 'var(--accent-yellow)',
                            display: 'block',
                          }}
                        >
                          +{fac.diasVencido}d vencida
                        </span>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          style={{ fontSize: 10, padding: '2px 6px', marginTop: 4 }}
                          onClick={() => navigate(fac.drillDownUrl)}
                        >
                          Cobrar →
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Panel>

      {/* ── SECCIÓN CENTRAL: GRÁFICOS ESTRATÉGICOS ── */}
      <div className="chart-row">
        <Panel
          titulo="Evolución histórica (Adjudicado vs Costos vs Cobrado)"
          subtitulo="Muestra si los contratos adjudicados superan saludablemente a los egresos reales"
        >
          {serie.isLoading && <Cargando />}
          {serie.data && serie.data.length > 0 && <GraficoTendencia datos={serie.data} />}
          {serie.data && serie.data.length === 0 && (
            <Vacio titulo="Todavía no hay movimientos registrados en el período" />
          )}
        </Panel>

        <Panel
          titulo="Antigüedad de la deuda del Estado (Aging)"
          subtitulo="Distribución de la cartera pendiente por tramo de mora"
        >
          {cobranza.tramos.every((tramo) => tramo.cantidad === 0) ? (
            <Vacio
              titulo="Sin facturas por cobrar"
              detalle="Cuando emitas facturas desde un proyecto, aparecerán acá ordenadas por antigüedad."
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {cobranza.tramos.map((tramo) => {
                const maximo = Math.max(...cobranza.tramos.map((t) => Math.abs(t.monto)), 1);
                const ancho = (Math.abs(tramo.monto) / maximo) * 100;
                const esVencido = tramo.clave !== 'POR_VENCER';

                return (
                  <div key={tramo.clave} style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                      }}
                    >
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>
                        {tramo.etiqueta}
                      </span>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                        <span style={{ fontSize: 10.5, color: 'var(--text-muted)' }}>
                          {tramo.cantidad} doc.
                        </span>
                        <span
                          className="td-mono"
                          style={{
                            fontSize: 13,
                            color:
                              esVencido && tramo.monto > 0
                                ? 'var(--accent-yellow)'
                                : 'var(--text-primary)',
                          }}
                        >
                          {formatearCLP(tramo.monto)}
                        </span>
                      </div>
                    </div>
                    <div className="budget-track">
                      <div
                        className="budget-fill"
                        style={{
                          width: `${ancho}%`,
                          background: esVencido
                            ? 'linear-gradient(90deg, var(--accent-yellow), #ff9800)'
                            : 'linear-gradient(90deg, var(--accent-green), var(--accent-blue))',
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>

      {/* ── DESEMPEÑO POR CATEGORÍA COMERCIAL ── */}
      <Panel
        titulo="Rentabilidad real por línea de negocio"
        subtitulo="Margen real obtenido versus el objetivo presupuestado por categoría comercial"
      >
        {categorias.isLoading && <Cargando />}
        {categorias.data && (
          <>
            <GraficoBarras
              etiquetas={categorias.data.map((categoria) => categoria.nombre)}
              series={[
                {
                  nombre: 'Ingresos',
                  datos: categorias.data.map((c) => c.ingresos),
                  color: 'rgba(33,150,243,.75)',
                },
                {
                  nombre: 'Costos',
                  datos: categorias.data.map((c) => c.costos),
                  color: 'rgba(211,47,47,.75)',
                },
              ]}
              altura={220}
            />

            <div className="tabla-scroll" style={{ marginTop: 18 }}>
              <table>
                <thead>
                  <tr>
                    <th>Categoría Comercial</th>
                    <th>Procesos</th>
                    <th>Win rate</th>
                    <th style={{ textAlign: 'right' }}>Ingresos</th>
                    <th style={{ textAlign: 'right' }}>Costos</th>
                    <th style={{ textAlign: 'right' }}>Margen real</th>
                    <th style={{ textAlign: 'right' }}>Meta</th>
                  </tr>
                </thead>
                <tbody>
                  {categorias.data.map((categoria) => {
                    const cumple =
                      categoria.margenRealPct !== null &&
                      categoria.margenRealPct >= categoria.margenObjetivoPct;

                    return (
                      <tr key={categoria.id}>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: 2,
                                background: categoria.color,
                              }}
                            />
                            <span style={{ color: 'var(--text-primary)' }}>{categoria.nombre}</span>
                          </span>
                        </td>
                        <td>{categoria.total}</td>
                        <td>
                          {categoria.winRatePct === null
                            ? '—'
                            : formatearPorcentajeSimple(categoria.winRatePct)}
                        </td>
                        <td className="td-mono">{formatearCLP(categoria.ingresos)}</td>
                        <td className="td-mono">{formatearCLP(categoria.costos)}</td>
                        <td
                          className="td-mono"
                          style={{
                            color:
                              categoria.margenRealPct === null
                                ? undefined
                                : cumple
                                  ? 'var(--accent-green)'
                                  : 'var(--accent-yellow)',
                            fontWeight: 600,
                          }}
                        >
                          {categoria.margenRealPct === null
                            ? '—'
                            : formatearPorcentajeSimple(categoria.margenRealPct)}
                        </td>
                        <td className="td-mono" style={{ color: 'var(--text-muted)' }}>
                          {categoria.margenObjetivoPct}%
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Panel>

      {/* ── MODAL DE INFORME EJECUTIVO PARA DIRECTORIO Y BANCOS ── */}
      <ModalInformeEjecutivo
        abierto={modalInformeAbierto}
        alCerrar={() => setModalInformeAbierto(false)}
        periodo={periodo}
        desde={esRangoPersonalizado ? fechaDesde || undefined : undefined}
        hasta={esRangoPersonalizado ? fechaHasta || undefined : undefined}
      />
    </div>
  );
}
